// 連携配信（フィードフォルダ）のセール商品に出す SALE ラベル。
//
// 表示可否は管理画面の feed_folder_filter.showSaleLabel で決まり、実際に出すかどうかは
// 「ラベル表示がON」かつ「その商品がセール中(isOnSale)」の両方を満たすときのみ
// （判定は feedFolderResolver.js が displayContent.showSaleLabel に畳んでいる）。
//
// 見た目はすべてインラインスタイルで与える。スライドの wrapper は DOM へ挿入される前に
// 組み立てられるため、生成時点の getRootNode() は shadow root を返さない。<style> を注入する
// 方式だと document.head 側に入り、後から shadow DOM へ入ったバッジには適用されず
// 「文字だけでラベルの体裁にならない」状態になる（実際にモバイルで発生した）。

import {
  applyFeedFolderSaleLabel,
  normalizeFeedFolderSaleLabelColor,
  FEED_FOLDER_SALE_LABEL_DEFAULT_TEXT_COLOR,
  FEED_FOLDER_SALE_LABEL_DEFAULT_BACKGROUND_COLOR,
} from '@/helpers/feedFolderSaleLabel';

export const FEED_FOLDER_SALE_BADGE_CLASS = 'storeeel-feed-sale-badge';
export const FEED_FOLDER_SALE_BADGE_MOBILE_CLASS = 'storeeel-feed-sale-badge--mobile';
// モバイルのストーリー型だけ、ヘッダーのグループ名の真下にラベルを出す
export const FEED_FOLDER_SALE_BADGE_STORY_CLASS = 'storeeel-feed-sale-badge--story';

const BASE_STYLE = {
  position: 'absolute',
  top: '16px',
  left: '16px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0 12px',
  // 箱の高さは固定する。上下paddingやフォント本来の行送りに任せると、埋め込み先の
  // font-family（バッジは指定を持たず継承する）で高さが変わり、サイトごとにラベルの
  // 大きさが違って見える。14px * 1.5em（line-height: normal の最大）でも収まる値。
  height: '22px',
  borderRadius: '999px',
  // 配色は管理画面（feed_folder_filter）で選んだ色を applyColors で上書きする。
  // ここに置いてある既定色は、色を選んでいない連携配信の見た目（従来のオレンジ）。
  background: FEED_FOLDER_SALE_LABEL_DEFAULT_BACKGROUND_COLOR,
  color: FEED_FOLDER_SALE_LABEL_DEFAULT_TEXT_COLOR,
  // 寸法は広告バッジ（adBadge.js）と揃える。同じ左上に出る2つのラベルで高さが違うと、
  // 広告と連携配信が混ざるキャンペーンで片方だけ沈んで見える
  fontSize: '14px',
  fontWeight: '700',
  // 行ボックスも font-size と同じ高さに固定する。normal だとフォント固有の行送り
  // （ascent+descent+lineGap）になり、上下非対称なぶんだけ文字が箱の中で上寄り／下寄りに
  // ずれる（メイリオの実測で 上0.73px / 下5.16px）。1 にすれば上下の half-leading が
  // 均等に削られ、align-items: center と合わせて文字が箱の中央に来る。
  // なお指定を消すのは不可。storeeelの枠は line-height: 0 を継承するため文字が潰れる。
  lineHeight: '1',
  letterSpacing: '0.08em',
  whiteSpace: 'nowrap',
  zIndex: '20',
  // 表示専用。CTAやスライド送りのタップを奪わない
  pointerEvents: 'none',
  userSelect: 'none',
};

// モバイルは wrapper が 100dvh のまま（枠のCSSを課さない）なので、wrapper基準に置くと
// 画面の隅に浮いてヘッダーと重なる。広告バッジ（adBadge.js）と同じく実座標で固定する。
const MOBILE_STYLE = {
  position: 'fixed',
  top: '48px',
  left: '4%',
  zIndex: '999999',
};

// ストーリー型はヘッダーにグループ名が乗る。ただしグループ名は shadow DOM の中にあり、
// 祖先(.storeeel-header)が transform を持つため position:fixed の基準がビューポートではなく
// その祖先になる。つまりCSS上の top 値と実際の表示位置がずれる。さらに文字サイズや端末で
// 高さも変わるため、静的な top では「グループ名の下」を狙っても簡単に被る。
// そこで実際に描画されたグループ名の下端を測って配置する（下の resolveStoryBadgeTop）。
//
// 寸法（padding / fontSize）は BASE_STYLE のまま＝広告バッジと同じ高さにする。以前はここだけ
// 小さくしていたが、同じ画面に出る広告ラベルとの高さの差が目立つため揃えた。位置は実測した
// グループ名の下端から取るので、高さが変わってもグループ名に被らない。
const MOBILE_STORY_STYLE = {
  position: 'fixed',
  left: '4%',
  zIndex: '999999',
};

// グループ名の下端からこれだけ空ける
const STORY_BADGE_GAP_PX = 8;
// グループ名がまだ描画されていない/空のときの退避値（ヘッダー高103pxの内側で広告バッジと同じ帯）
const STORY_BADGE_FALLBACK_TOP_PX = 56;

const isMobileStoryFrame = () =>
  Boolean(window?.storeeelItems?.isMobile) && window?.storeeelItems?.frame === 'circle';

const createBadge = () => {
  const badge = document.createElement('div');
  badge.className = FEED_FOLDER_SALE_BADGE_CLASS;
  return badge;
};

// 実際に描画されているグループ名の下端を測る。ヘッダーは shadow DOM なので shadowRoot 経由で引く。
//
// ヘッダーはグループごとに作られ（groupSwipeAction.js が各スライドへ prepend する）、
// DOM 上に複数存在する。画面外のスライドにあるヘッダーを測ると本来より下の値が返るため、
// 「いま画面内に見えているグループ名」だけを対象にする。
// 取れない（未描画・グループ名が空）場合は null を返し、呼び出し側が退避値を使う。
const resolveStoryBadgeTop = () => {
  if (typeof document === 'undefined') return null;

  const viewportHeight = window?.innerHeight || 0;
  const hosts = Array.from(document.querySelectorAll('[data-storeeel-header-host]'));

  for (const host of hosts) {
    const groupName = host.shadowRoot?.querySelector?.('.storeeel-group-name');
    const rect = groupName?.getBoundingClientRect?.();
    if (!rect || !rect.height) continue;
    // 画面外（上に流れた／下に控えている）スライドのヘッダーは採用しない
    if (rect.bottom <= 0 || (viewportHeight && rect.bottom > viewportHeight)) continue;

    return Math.round(rect.bottom + STORY_BADGE_GAP_PX);
  }

  return null;
};

// 一度決めた位置は保持する。コンテンツを送るたびに測り直すと、swiper のスライドが持つ
// transform の影響で測定対象のヘッダーが変わり、ラベルがだんだん下へずれていくため。
let cachedStoryBadgeTop = null;
let storyBadgeResizeBound = false;

// 画面サイズが変わればヘッダーの位置も変わるので、そのときだけ測り直す
const bindStoryBadgeResizeReset = () => {
  if (storyBadgeResizeBound || typeof window?.addEventListener !== 'function') return;

  storyBadgeResizeBound = true;
  window.addEventListener('resize', () => { cachedStoryBadgeTop = null; });
};

const applyStoryTop = (badge) => {
  bindStoryBadgeResizeReset();
  if (cachedStoryBadgeTop === null) cachedStoryBadgeTop = resolveStoryBadgeTop();

  badge.style.top = `${cachedStoryBadgeTop ?? STORY_BADGE_FALLBACK_TOP_PX}px`;
};

// 配色は連携配信ごとに変わる。同じバッジ要素が別の連携配信の描画へ使い回されても前の色が
// 残らないよう、表示のたびに必ず入れ直す（配置と同じ考え方）。
const applyColors = (badge, content) => {
  badge.style.color = normalizeFeedFolderSaleLabelColor(
    content?.saleLabelTextColor,
    FEED_FOLDER_SALE_LABEL_DEFAULT_TEXT_COLOR,
  );
  badge.style.background = normalizeFeedFolderSaleLabelColor(
    content?.saleLabelBackgroundColor,
    FEED_FOLDER_SALE_LABEL_DEFAULT_BACKGROUND_COLOR,
  );
};

// 配置はフレームと端末で変わるため、毎回ベース＋差分の順で上書きし直す
// （同じバッジが別条件で再利用されても前の指定が残らない）。
const applyPlacement = (badge) => {
  const isMobile = Boolean(window?.storeeelItems?.isMobile);
  const isStory = isMobileStoryFrame();

  Object.assign(badge.style, BASE_STYLE);
  if (isMobile) Object.assign(badge.style, isStory ? MOBILE_STORY_STYLE : MOBILE_STYLE);

  badge.classList.toggle(FEED_FOLDER_SALE_BADGE_MOBILE_CLASS, isMobile);
  badge.classList.toggle(FEED_FOLDER_SALE_BADGE_STORY_CLASS, isStory);

  if (!isStory) return;

  applyStoryTop(badge);
  // スライド生成時はヘッダーがまだ組み上がっていないことがある。
  // 次フレームで測り直して、初回だけ退避値のまま残るのを防ぐ。
  if (typeof window?.requestAnimationFrame === 'function') {
    window.requestAnimationFrame(() => {
      if (badge.isConnected) applyStoryTop(badge);
    });
  }
};

/**
 * wrapper にセールバッジを付け外しする。
 * 同じ wrapper が通常コンテンツの描画へ再利用されたときに前の商品のバッジが残らないよう、
 * 表示しない場合は必ず取り除く（呼び出し側は毎回この関数を通すこと）。
 *
 * 表示可否（showSaleLabel）と中身（saleLabel）は必ずセットで効くため、両者を別々の引数で
 * 受け取らず content ごと渡してもらう。以前は中身を第3引数にしていたが、呼び出し側の一方が
 * 渡し忘れても黙って 'SALE' になるだけで、グループ内を送るたびにラベルが化けた
 * （feedFolderAspectFrame.js の取りこぼし）。片方だけ欠ける状態を構造的に無くす。
 */
export const setFeedFolderSaleBadge = (wrapper, content) => {
  if (!wrapper) return;

  const existing = wrapper.querySelector(`.${FEED_FOLDER_SALE_BADGE_CLASS}`);

  if (!content?.showSaleLabel) {
    if (existing) existing.remove();
    return;
  }

  if (typeof getComputedStyle === 'function' && getComputedStyle(wrapper).position === 'static') {
    wrapper.style.position = 'relative';
  }

  const badge = existing || wrapper.appendChild(createBadge());
  // バッジを使い回すときに前の商品の価格が残らないよう、表示のたびに必ず入れ直す
  applyFeedFolderSaleLabel(badge, content.saleLabel);
  applyPlacement(badge);
  // 配置（BASE_STYLE）が既定色を書き戻すため、配色は必ずその後に当てる
  applyColors(badge, content);
};
